import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Boost.scss";
import { useToast } from "../../Alerts/Toast";
import { getCurrentUserId, getAccessToken } from "../../../awsAuth";
import { photoVariant } from "../../../utils/imageUrl";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

async function fetchJson(url, options) {
  const res = await fetch(url, options);
  const ct = res.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");

  let data;
  if (isJson) {
    try { data = await res.json(); } catch { data = null; }
  } else {
    const raw = await res.text();
    data = { __raw: raw };
  }

  if (!res.ok) {
    const rawMsg =
      (data && data.message) ||
      (data && data.__raw && data.__raw.slice(0, 160)) ||
      `HTTP ${res.status}`;
    const err = new Error(`${res.status} ${res.statusText}: ${rawMsg}`);
    err.status = res.status;
    err.url = url;
    err.data = data;
    throw err;
  }
  return data;
}

async function fetchWithBoostFallback(path, options) {
  try {
    return await fetchJson(`${API_BASE}/api/boost/${path}`, options);
  } catch (e) {
    if (e && e.status === 404) {
      console.info(`[Boost] 404 на /api/boost/${path} → fallback на /api/raise/${path}`);
      return await fetchJson(`${API_BASE}/api/raise/${path}`, options);
    }
    throw e;
  }
}

const DURATIONS = [
  { key: "24h", label: "24 години", hours: 24, priceMinorFallback: 4900 },
  { key: "3d",  label: "3 дні",     hours: 72, priceMinorFallback: 12900 },
  { key: "7d",  label: "7 днів",    hours: 168, priceMinorFallback: 24900 },
];

function fmtMoney(minor) {
  if (typeof minor !== "number") return "—";
  return (minor / 100).toFixed(2) + " ₴";
}

function timeLeftFromEndsAtSec(endsAtSec) {
  if (!endsAtSec) return "—";
  const now = Date.now();
  const t = Number(endsAtSec) * 1000;
  const diff = t - now;
  if (diff <= 0) return "завершено";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return `${d} дн ${rh} год`;
  }
  return `${h} год ${m} хв`;
}

const Boost = ({ onWalletRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(null);
  // PACKAGE #12 Phase C — low-friction Boost auto-topup. When /start comes
  // back 402 INSUFFICIENT_FUNDS, this holds exactly which listing/duration
  // triggered it so an inline "Поповнити X ₴ і підняти" prompt can render
  // right under that row instead of sending the user to Wallet and back.
  const [insufficientPrompt, setInsufficientPrompt] = useState(null); // { listingId, hours, need } | null
  const [toppingUp, setToppingUp] = useState(null); // listingId currently creating a top-up invoice
  const toast = useToast();
  const uid = getCurrentUserId?.();

  const didInit = useRef(false);

  const [summaryTab, setSummaryTab] = useState("all");

  const getListingId = (item) => {
    return (
      item?.listingId ||
      item?.id ||
      item?._id ||
      item?.pk ||
      item?.PK ||
      null
    );
  };

  const getDurationPriceMinor = (item, d) => {
    const byHours = item?.raisePriceMinorByHours && (item.raisePriceMinorByHours[String(d.hours)] ?? item.raisePriceMinorByHours[d.hours]);
    if (typeof byHours === "number") return byHours;

    const byKey = item?.raisePricesMinor && (item.raisePricesMinor[d.key] ?? item.raisePricesMinor[String(d.hours)]);
    if (typeof byKey === "number") return byKey;

    const byFields =
      (d.key === "24h" && typeof item?.raisePriceMinor24h === "number" && item.raisePriceMinor24h) ||
      (d.key === "3d"  && typeof item?.raisePriceMinor3d === "number"  && item.raisePriceMinor3d) ||
      (d.key === "7d"  && typeof item?.raisePriceMinor7d === "number"  && item.raisePriceMinor7d);

    if (typeof byFields === "number") return byFields;

    return d.priceMinorFallback;
  };

  const attachBoostStatuses = async (items) => {
    const nowSec = Math.floor(Date.now() / 1000);

    const statuses = await Promise.all(
      items.map(async (it) => {
        const listingId = getListingId(it);
        if (!listingId) return { listingId: null, active: false, endsAtSec: null, plan: null, status: null };

        try {
          const s = await fetchWithBoostFallback(`status/${listingId}`);

          const endsAtSec = Number(s?.endsAtSec || 0) || null;
          const active = !!endsAtSec && endsAtSec > nowSec;

          return {
            listingId,
            active,
            endsAtSec,
            plan: s?.plan || null,
            status: s?.status || null,
          };
        } catch (e) {
          return { listingId, active: false, endsAtSec: null, plan: null, status: null };
        }
      })
    );

    const map = new Map(statuses.filter(x => x.listingId).map(x => [x.listingId, x]));

    return items.map((it) => {
      const id = getListingId(it);
      const st = id ? map.get(id) : null;

      return {
        ...it,
        boostActive: !!st?.active,
        boostEndsAtSec: st?.endsAtSec || null,
        boostPlan: st?.plan || null,
        boostStatus: st?.status || null,
      };
    });
  };

  const fetchMyListings = async () => {
    if (!uid) {
      const msg = "Потрібно увійти в акаунт, щоб керувати підняттями.";
      setError(msg);
      toast.info(msg, 3000);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");

    try {
      const token = await getAccessToken().catch(() => null);

      if (!token) {
        throw new Error("Потрібна авторизація");
      }

      const data = await fetchWithBoostFallback("my-listings", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const baseItems = data?.data || [];
      const merged = await attachBoostStatuses(baseItems);

      setListings(merged);
    } catch (e) {
      const msg = e?.message || "Сталася помилка під час завантаження";
      setError(msg);
      toast.error(msg, 3200);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    fetchMyListings();
  }, [uid]);

  const totals = useMemo(() => {
    let spent = 0;
    let active = 0;
    listings.forEach(l => {
      if (l.raiseSpentMinor) spent += l.raiseSpentMinor;
      if (l.boostActive) active += 1;
    });
    return { spent, active, count: listings.length };
  }, [listings]);

  const filteredListings = useMemo(() => {
    if (summaryTab === "active") {
      return listings.filter(l => l.boostActive);
    }
    return listings;
  }, [listings, summaryTab]);

  const startBoost = async (listingId, hours) => {
    if (!uid) {
      const msg = "Потрібно увійти в акаунт, щоб підняти оголошення.";
      setError(msg);
      toast.info(msg, 3000);
      return;
    }

    if (!listingId) {
      const msg = "Помилка: не знайдено listingId для цього оголошення.";
      setError(msg);
      toast.error(msg, 3200);
      return;
    }

    setStarting(listingId);
    setError("");
    setInsufficientPrompt(null);

    try {
      const token = await getAccessToken().catch(() => null);

      if (!token) {
        const msg = "Сесія недійсна. Увійдіть повторно.";
        setError(msg);
        toast.error(msg, 3200);
        return;
      }

      const requestId =
        (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const r = await fetchWithBoostFallback("start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ listingId, durationHours: hours, requestId })
      });

      await fetchMyListings();
      toast.success("Підняття активовано", 2200);

      try { onWalletRefresh?.(); } catch {}

      if (r?.walletCoins != null || r?.walletCoinsAfter != null) {
        try { onWalletRefresh?.(); } catch {}
      }
    } catch (e) {
      const status = e?.status;
      const serverMsg = e?.data?.message || "";
      const need = e?.data?.need;

      if (status === 402 && (serverMsg === "INSUFFICIENT_FUNDS" || String(serverMsg).includes("INSUFFICIENT_FUNDS"))) {
        const needNum = Number(need);
        // Instead of sending the user off to Wallet and back, offer to pay
        // the exact shortfall inline and resume this exact boost the
        // moment the top-up is confirmed — see topupAndBoost() below.
        setInsufficientPrompt({ listingId, hours, need: Number.isFinite(needNum) ? needNum : null });
        return;
        }

      const msg = e?.message || "Не вдалося підняти оголошення";
      setError(msg);
      toast.error(msg, 3200);
        } finally {
          setStarting(null);
        }
        };

  // PACKAGE #12 Phase C — creates a real Monobank invoice for exactly the
  // wallet shortfall (server-derived, never trusts anything computed here)
  // and redirects to the hosted checkout. The webhook-driven fulfillment
  // path (server) auto-resumes THIS exact boost once the top-up is
  // confirmed; PaymentReturn.js also calls the resume-intent endpoint as a
  // safety net in case that automatic attempt never happened.
  const topupAndBoost = async (listingId, hours) => {
    setToppingUp(listingId);
    setError("");

    try {
      const token = await getAccessToken().catch(() => null);

      if (!token) {
        const msg = "Сесія недійсна. Увійдіть повторно.";
        setError(msg);
        toast.error(msg, 3200);
        return;
      }

      const requestId =
        (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const r = await fetchWithBoostFallback("topup-and-start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ listingId, durationHours: hours, requestId }),
      });

      if (!r?.redirectUrl) {
        throw new Error("Не вдалося створити рахунок на оплату");
      }

      try {
        localStorage.setItem("cvanto_pending_boost_listing_id", String(listingId));
      } catch {}

      window.location.href = r.redirectUrl;
    } catch (e) {
      const msg = e?.message || "Не вдалося створити платіж";
      setError(msg);
      toast.error(msg, 3200);
      setToppingUp(null);
    }
  };

  const onKpiKey = (e, tab) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setSummaryTab(tab);
    }
  };

  return (
    <div className="boost">
      <div className="boost-summary">
        <div className="card">
          <div
            className={`kpi clickable ${summaryTab === "all" ? "active" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => setSummaryTab("all")}
            onKeyDown={(e) => onKpiKey(e, "all")}
            title="Показати всі оголошення"
          >
            <div className="value">{totals.count}</div>
            <div className="label">Ваші оголошення</div>
          </div>

          <div
            className={`kpi clickable ${summaryTab === "active" ? "active" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => setSummaryTab("active")}
            onKeyDown={(e) => onKpiKey(e, "active")}
            title="Показати лише активні підняття"
          >
            <div className="value">{totals.active}</div>
            <div className="label">Активних піднять</div>
          </div>

          <div className="kpi">
            <div className="value">{fmtMoney(totals.spent)}</div>
            <div className="label">Витрачено загалом</div>
          </div>
        </div>
      </div>

      {false && error && <div className="error">{error}</div>}

      {loading ? (
        <div className="loader">Завантаження…</div>
      ) : (
        <div className="boost-list">
          {(!error && filteredListings.length === 0) ? (
            <div className="empty">
              {summaryTab === "active" ? "Немає активних піднять." : "Немає оголошень для підняття."}
            </div>
          ) : filteredListings.map((item, idx) => {
            const listingId = getListingId(item);
            const active = !!item.boostActive;

            return (
              <div className="boost-row" key={listingId || item.createdAt || idx}>
                <div className="left">
                  <div className="thumb">
                    {item.photos && item.photos[0] ? (
                      <img src={photoVariant(item.photos[0], "xs")} alt="" loading="lazy" decoding="async" />
                    ) : (
                      <div className="ph" />
                    )}
                  </div>

                  <div className="meta">
                    <div className="title" title={item.title || "Оголошення"}>
                      {item.title || "Оголошення"}
                    </div>

                    <div className="sub">
                      {active ? (
                        <span className="raisePill">
                          <span className="raisePillMain">Оголошення підняте</span>
                          <span className="raisePillSub">
                            залишилось {timeLeftFromEndsAtSec(item.boostEndsAtSec)}
                          </span>
                        </span>
                      ) : (
                        <span className="inactive">Зараз не піднято</span>
                      )}
                    </div>

                    {active && item.boostPlan && (
                      <div className="sub mut" style={{ marginTop: 4 }}>
                        План: {item.boostPlan}
                      </div>
                    )}

                    {!listingId && (
                      <div className="sub mut" style={{ opacity: 0.8 }}>
                        ⚠️ Нема listingId у відповіді бекенда
                      </div>
                    )}
                  </div>
                </div>

                <div className="right">
                  <div className="plans">
                    {DURATIONS.map(d => (
                      <button
                        key={d.key}
                        type="button"
                        disabled={starting === listingId || toppingUp === listingId || !listingId}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          startBoost(listingId, d.hours);
                        }}
                        title={!listingId ? "Немає listingId (бекенд має повертати id)" : "Підняти оголошення"}
                      >
                        <span className="planLabel">{d.label}</span>
                        <span className="planPrice">{fmtMoney(getDurationPriceMinor(item, d))}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {insufficientPrompt && insufficientPrompt.listingId === listingId && (
                  <div className="boost-topup-prompt">
                    <span className="boost-topup-prompt-text">
                      Недостатньо Tokens для підняття
                      {Number.isFinite(insufficientPrompt.need) ? ` (потрібно ${insufficientPrompt.need})` : ""}.
                    </span>
                    <div className="boost-topup-prompt-actions">
                      <button
                        type="button"
                        className="boost-topup-prompt-cancel"
                        onClick={() => setInsufficientPrompt(null)}
                        disabled={toppingUp === listingId}
                      >
                        Скасувати
                      </button>
                      <button
                        type="button"
                        className="boost-topup-prompt-pay"
                        disabled={toppingUp === listingId}
                        onClick={() => topupAndBoost(listingId, insufficientPrompt.hours)}
                      >
                        {toppingUp === listingId ? "Створюємо рахунок…" : "Поповнити і підняти"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Boost;
